import { useEffect, useState } from 'react';
import { AlertCircle, X, Copy, Check } from 'lucide-react';
import api from '../../../utils/api';

const DISMISS_KEY = 'dealshaq.pushOptInNudge.dismissedAt';
const DISMISS_WINDOW_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const COVERAGE_THRESHOLD = 30;  // %

/**
 * PushOptInNudge — soft banner shown on RetailerDashboard when the
 * DRLP's ``device_coverage_rate`` is below 30%.
 *
 * The message reframes low delivery-rate as a customer-side opt-in
 * issue (not a backend problem) and gives the retailer a copy-paste
 * checkout snippet to prompt consumers.
 *
 * Dismissible: user can hide for 7 days via localStorage.
 * Also silently hides when the analytics endpoint 401s or the widget
 * has no data yet.
 */
const SNIPPET = 'Want notified the second your order is ready? Say "yes" when your browser asks to send you push notifications on your next order confirmation.';

export default function PushOptInNudge() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Respect prior dismissal.
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      const ts = raw ? parseInt(raw, 10) : 0;
      if (ts && (Date.now() - ts) < DISMISS_WINDOW_MS) {
        setDismissed(true);
        return;
      }
    } catch { /* ignore malformed */ }

    let alive = true;
    (async () => {
      try {
        const res = await api.get('/retailer/analytics/push-open-rate', { params: { days: 30 } });
        if (alive) setData(res.data);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, []);

  const onDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setDismissed(true);
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(SNIPPET);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked */ }
  };

  if (dismissed) return null;
  if (!data || data.total_events === 0) return null;
  if (data.device_coverage_rate >= COVERAGE_THRESHOLD) return null;

  return (
    <div
      className="rounded-lg border border-amber-200 bg-amber-50 p-4"
      data-testid="push-optin-nudge"
    >
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-900" data-testid="push-optin-nudge-title">
            Most of your customers haven&apos;t opted in yet
            <span className="ml-2 text-xs font-normal text-amber-700">
              ({data.device_coverage_rate}% coverage · pilot goal is 30%+)
            </span>
          </p>
          <p className="text-xs text-amber-800 mt-1">
            Low delivery rates are a customer-side signal — they haven&apos;t clicked the bell on
            their order page. Paste this line into your next checkout email or POS
            receipt footer to nudge them:
          </p>
          <div className="mt-3 flex items-start gap-2">
            <div className="flex-1 rounded border border-amber-200 bg-white px-3 py-2 text-xs text-slate-700 font-mono"
                 data-testid="push-optin-nudge-snippet">
              {SNIPPET}
            </div>
            <button
              type="button"
              onClick={onCopy}
              className="shrink-0 rounded border border-amber-300 bg-white hover:bg-amber-100 px-3 py-2 text-xs font-medium text-amber-900 flex items-center gap-1"
              data-testid="push-optin-nudge-copy-btn"
            >
              {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss nudge for 7 days"
          className="text-amber-500 hover:text-amber-800 shrink-0"
          data-testid="push-optin-nudge-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
